import { useState } from 'react';
import { churchApi, errorMessage, isConflict, CHURCH_ACTION_LABEL } from './applicationStatusApi';
import { ConfirmDialog } from './applicationUi';

// 교회 화면의 원클릭 상태 변경 (application-status-design.md 6.2)
// - 서버가 계산한 actions(가능한 다음 상태)만 버튼으로 그린다
// - 불합격은 분리해서 확인을 한 번 거치고, 연락·면접·제안은 메모(선택)를 받는다
// - 보류·검토 재개는 즉시 실행

type Props = {
  applicationId: number;
  version: number;
  actions: string[];
  applicantName: string;
  small?: boolean;
  onChanged: () => void;
};

const NEEDS_DIALOG = ['CONTACTED', 'INTERVIEW', 'OFFERED', 'REJECTED'];

const DIALOG_COPY: Record<string, { title: string; desc: string; placeholder: string; confirm: string }> = {
  CONTACTED: {
    title: '연락 상태로 바꿀까요?',
    desc: '지원자에게 알림이 전송되고, 이 시점부터 실명과 연락처가 공개됩니다.',
    placeholder: '지원자에게 전할 메모 (선택) — 예: 10월 2주 면담 가능하신지 확인 부탁드립니다.',
    confirm: '연락하기',
  },
  INTERVIEW: {
    title: '면접 일정을 안내할까요?',
    desc: '지원자에게 면접 알림이 전송됩니다.',
    placeholder: '장소·준비물 등 메모 (선택)',
    confirm: '면접 안내',
  },
  OFFERED: {
    title: '청빙을 제안할까요?',
    desc: '지원자에게 청빙 제안 알림이 전송되고, 지원자는 수락 또는 거절로 응답합니다.',
    placeholder: '제안 메시지 (권장)',
    confirm: '청빙 제안',
  },
  REJECTED: {
    title: '불합격 처리할까요?',
    desc: '지원자에게 결과 알림이 전송됩니다. 실수라면 24시간 안에 되돌릴 수 있습니다.',
    placeholder: '사유 (선택, 지원자에게는 보이지 않습니다)',
    confirm: '불합격',
  },
};

export default function ChurchStatusActions({ applicationId, version, actions, applicantName, small, onChanged }: Props) {
  const [dialog, setDialog] = useState<string | null>(null);
  const [memo, setMemo] = useState('');
  const [interviewAt, setInterviewAt] = useState('');
  const [busy, setBusy] = useState(false);

  const handleError = (e: unknown) => {
    alert(errorMessage(e));
    if (isConflict(e)) onChanged();
  };

  const change = async (to: string, extra: { memo?: string; interviewAt?: string } = {}) => {
    setBusy(true);
    try {
      await churchApi.changeStatus(applicationId, { to, version, ...extra });
      setDialog(null);
      setMemo('');
      setInterviewAt('');
      onChanged();
    } catch (e) {
      setDialog(null);
      handleError(e);
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    setBusy(true);
    try {
      await churchApi.undo(applicationId);
      setDialog(null);
      onChanged();
    } catch (e) {
      setDialog(null);
      handleError(e);
    } finally {
      setBusy(false);
    }
  };

  const progress = actions.filter((a) => CHURCH_ACTION_LABEL[a] && a !== 'REJECTED');
  const cls = `app-btn${small ? ' app-btn--small' : ''}`;
  const copy = dialog ? DIALOG_COPY[dialog] : null;

  return (
    <>
      {progress.map((to) => (
        <button
          type="button"
          key={to}
          className={`${cls}${to === 'OFFERED' ? ' app-btn--accent' : ''}`}
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            if (NEEDS_DIALOG.includes(to)) setDialog(to);
            else change(to);
          }}
        >
          {CHURCH_ACTION_LABEL[to]}
        </button>
      ))}
      {actions.includes('REJECTED') && (
        <button
          type="button"
          className={`${cls} app-btn--danger-outline is-separated`}
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            setDialog('REJECTED');
          }}
        >
          불합격
        </button>
      )}
      {actions.includes('UNDO') && (
        <button
          type="button"
          className={`${cls} is-separated`}
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            setDialog('UNDO');
          }}
        >
          되돌리기
        </button>
      )}

      {copy && dialog && (
        <ConfirmDialog
          title={copy.title}
          confirmLabel={copy.confirm}
          danger={dialog === 'REJECTED'}
          busy={busy}
          onCancel={() => setDialog(null)}
          onConfirm={() => change(dialog, { memo: memo.trim() || undefined, interviewAt: dialog === 'INTERVIEW' && interviewAt ? new Date(interviewAt).toISOString() : undefined })}
        >
          <p>
            <b>{applicantName}</b>님 — {copy.desc}
          </p>
          {dialog === 'INTERVIEW' && <input type="datetime-local" value={interviewAt} onChange={(e) => setInterviewAt(e.target.value)} />}
          <textarea rows={3} value={memo} placeholder={copy.placeholder} maxLength={1000} onChange={(e) => setMemo(e.target.value)} />
        </ConfirmDialog>
      )}
      {dialog === 'UNDO' && (
        <ConfirmDialog title="직전 변경을 되돌릴까요?" confirmLabel="되돌리기" busy={busy} onCancel={() => setDialog(null)} onConfirm={undo}>
          <p>되돌린 사실은 기록에 남으며, 지원자에게 이미 알림이 간 경우 정정 알림이 전송됩니다.</p>
        </ConfirmDialog>
      )}
    </>
  );
}
